// What chess.com's public API says about a game, and how it becomes a
// GameRecord. Only the fields the record needs are typed.
import { type GameRecord, type GameResult } from '@chess/api-model';

export const CHESSCOM_API = 'https://api.chess.com/pub';

export interface ChesscomPlayer {
  username: string;
  rating?: number;
  /** win, checkmated, resigned, timeout, agreed, repetition, stalemate, ... */
  result?: string;
}

export interface ChesscomGame {
  url: string;
  pgn: string;
  uuid?: string;
  /** Seconds, or "base+increment", or "1/86400" for daily games. */
  time_control?: string;
  time_class?: string;
  /** Unix seconds. */
  end_time?: number;
  white: ChesscomPlayer;
  black: ChesscomPlayer;
}

export function archivesUrl(username: string): string {
  return `${CHESSCOM_API}/player/${encodeURIComponent(username)}/games/archives`;
}

/** "2024/03" from ".../games/2024/03". */
export function monthOf(archiveUrl: string): string {
  return archiveUrl.split('/').slice(-2).join('/');
}

const DRAWS = new Set(['agreed', 'repetition', 'stalemate', 'insufficient', '50move', 'timevsinsufficient']);

/** The result, from the PGN header when it has one, else from the sides' outcomes. */
export function resultOf(game: ChesscomGame): GameResult {
  const header = /\[Result "([^"]+)"\]/.exec(game.pgn ?? '')?.[1];
  if (header === '1-0' || header === '0-1' || header === '1/2-1/2') return header;
  if (game.white.result === 'win') return '1-0';
  if (game.black.result === 'win') return '0-1';
  if (game.white.result && DRAWS.has(game.white.result)) return '1/2-1/2';
  return '*';
}

/** "300" -> "5+0", "180+2" -> "3+2", "1/86400" -> "daily". */
export function formatTimeControl(game: ChesscomGame): string | undefined {
  const tc = game.time_control;
  if (!tc) return game.time_class;
  if (tc.includes('/')) return 'daily';
  const [base, inc = '0'] = tc.split('+');
  const seconds = Number(base);
  const minutes = seconds % 60 === 0 ? `${seconds / 60}` : `${seconds}s`;
  return `${minutes}+${inc}`;
}

export function gameId(game: ChesscomGame): string {
  if (game.uuid) return game.uuid;
  const tail = game.url.split('/').filter(Boolean).pop() ?? '';
  return `chesscom-${tail}`;
}

export function toGameRecord(game: ChesscomGame, owner: string, importedAt: string): GameRecord {
  return {
    schemaVersion: 1,
    id: gameId(game),
    owner,
    white: { name: game.white.username, rating: game.white.rating },
    black: { name: game.black.username, rating: game.black.rating },
    result: resultOf(game),
    playedAt: game.end_time ? new Date(game.end_time * 1000).toISOString() : importedAt,
    importedAt,
    source: 'chess.com',
    url: game.url,
    timeControl: formatTimeControl(game),
    pgn: game.pgn,
  };
}
